/* ==========================================================================
   Hydreeva — interacciones propias (vanilla, sin librerías)
   Un init por componente. Cada uno sale si su elemento no existe.
   ========================================================================== */

(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --- Reveals al hacer scroll (observador compartido) ------------------ */

  var revealObserver = null;

  function initReveals(raiz) {
    var elementos = (raiz || document).querySelectorAll('.mt-reveal:not(.mt-visible)');
    if (!elementos.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      elementos.forEach(function (el) { el.classList.add('mt-visible'); });
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entradas) {
        entradas.forEach(function (entrada) {
          if (!entrada.isIntersecting) return;
          entrada.target.classList.add('mt-visible');
          revealObserver.unobserve(entrada.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    }

    elementos.forEach(function (el) { revealObserver.observe(el); });
  }

  /* --- Cifras que cuentan hacia arriba --------------------------------- */

  function initContadores(raiz) {
    var cifras = (raiz || document).querySelectorAll('.mt-cifra[data-mt-hasta]');
    if (!cifras.length) return;

    function pintar(el, valor, decimales) {
      var texto = decimales > 0 ? valor.toFixed(decimales) : Math.round(valor).toString();
      el.textContent = texto.replace('.', ',');
    }

    function animar(el) {
      var hasta = parseFloat(el.getAttribute('data-mt-hasta'));
      if (isNaN(hasta)) return;
      var decimales = parseInt(el.getAttribute('data-mt-decimales') || '0', 10);
      if (reduceMotion) { pintar(el, hasta, decimales); return; }

      var duracion = 1600;
      var inicio = null;

      function paso(t) {
        if (inicio === null) inicio = t;
        var p = Math.min((t - inicio) / duracion, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        pintar(el, hasta * eased, decimales);
        if (p < 1) requestAnimationFrame(paso);
      }
      requestAnimationFrame(paso);
    }

    if (!('IntersectionObserver' in window)) {
      cifras.forEach(animar);
      return;
    }

    var obs = new IntersectionObserver(function (entradas) {
      entradas.forEach(function (e) {
        if (!e.isIntersecting) return;
        animar(e.target);
        obs.unobserve(e.target);
      });
    }, { threshold: 0.5 });

    cifras.forEach(function (el) { obs.observe(el); });
  }

  /* --- Comparador antes / después con deslizador ------------------------ */

  function initComparadores(raiz) {
    var comparadores = (raiz || document).querySelectorAll('.mt-comparador');
    if (!comparadores.length) return;

    comparadores.forEach(function (comp) {
      if (comp.dataset.mtListo === '1') return;
      comp.dataset.mtListo = '1';

      var asa = comp.querySelector('.mt-comparador-asa');
      var capa = comp.querySelector('.mt-comparador-capa');
      if (!asa || !capa) return;

      var arrastrando = false;

      function fijar(porcentaje) {
        var p = Math.max(0, Math.min(100, porcentaje));
        comp.style.setProperty('--mt-corte', p + '%');
        asa.setAttribute('aria-valuenow', Math.round(p));
      }

      function desdeEvento(e) {
        var rect = comp.getBoundingClientRect();
        var x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
        fijar((x / rect.width) * 100);
      }

      function empezar(e) {
        arrastrando = true;
        comp.classList.add('mt-comparador--activo');
        desdeEvento(e);
      }
      function mover(e) {
        if (!arrastrando) return;
        if (e.cancelable) e.preventDefault();
        desdeEvento(e);
      }
      function terminar() {
        arrastrando = false;
        comp.classList.remove('mt-comparador--activo');
      }

      comp.addEventListener('mousedown', empezar);
      comp.addEventListener('touchstart', empezar, { passive: true });
      window.addEventListener('mousemove', mover);
      window.addEventListener('touchmove', mover, { passive: false });
      window.addEventListener('mouseup', terminar);
      window.addEventListener('touchend', terminar);

      asa.addEventListener('keydown', function (e) {
        var actual = parseFloat(asa.getAttribute('aria-valuenow')) || 50;
        if (e.key === 'ArrowLeft') { fijar(actual - 4); e.preventDefault(); }
        if (e.key === 'ArrowRight') { fijar(actual + 4); e.preventDefault(); }
      });

      fijar(parseFloat(comp.dataset.mtInicio || '50'));
    });
  }

  /* --- Pestañas (modos de chorro, etc.) --------------------------------- */

  function initPestanas(raiz) {
    var grupos = (raiz || document).querySelectorAll('.mt-pestanas');
    if (!grupos.length) return;

    grupos.forEach(function (grupo) {
      if (grupo.dataset.mtListo === '1') return;
      grupo.dataset.mtListo = '1';

      var botones = Array.prototype.slice.call(grupo.querySelectorAll('.mt-pestana'));
      var paneles = Array.prototype.slice.call(grupo.querySelectorAll('.mt-panel'));
      if (!botones.length) return;

      function activar(indice) {
        botones.forEach(function (b, i) {
          var on = i === indice;
          b.classList.toggle('mt-pestana--activa', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
          b.setAttribute('tabindex', on ? '0' : '-1');
        });
        paneles.forEach(function (p, i) {
          p.classList.toggle('mt-panel--activo', i === indice);
          p.hidden = i !== indice;
        });
      }

      botones.forEach(function (b, i) {
        b.addEventListener('click', function () { activar(i); });
        b.addEventListener('keydown', function (e) {
          var siguiente = null;
          if (e.key === 'ArrowRight') siguiente = (i + 1) % botones.length;
          if (e.key === 'ArrowLeft') siguiente = (i - 1 + botones.length) % botones.length;
          if (siguiente === null) return;
          e.preventDefault();
          activar(siguiente);
          botones[siguiente].focus();
        });
      });

      activar(0);
    });
  }

  /* --- Selector visual de color ----------------------------------------- */

  function initSelectorColor(raiz) {
    var selectores = (raiz || document).querySelectorAll('.mt-colores');
    if (!selectores.length) return;

    selectores.forEach(function (sel) {
      if (sel.dataset.mtListo === '1') return;
      sel.dataset.mtListo = '1';

      var muestras = Array.prototype.slice.call(sel.querySelectorAll('.mt-muestra'));
      var vistas = Array.prototype.slice.call(sel.querySelectorAll('.mt-color-vista'));
      var nombre = sel.querySelector('.mt-color-nombre');
      if (!muestras.length) return;

      function activar(i) {
        muestras.forEach(function (m, j) {
          var on = j === i;
          m.classList.toggle('mt-muestra--activa', on);
          m.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        vistas.forEach(function (v, j) {
          v.classList.toggle('mt-color-vista--activa', j === i);
        });
        if (nombre && muestras[i].dataset.mtNombre) {
          nombre.textContent = muestras[i].dataset.mtNombre;
        }
        var acento = muestras[i].dataset.mtColor;
        if (acento) sel.style.setProperty('--mt-color-activo', acento);
      }

      muestras.forEach(function (m, i) {
        m.addEventListener('click', function () { activar(i); });
      });

      activar(0);
    });
  }

  /* --- Acordeón de preguntas frecuentes --------------------------------- */

  function initAcordeon(raiz) {
    var acordeones = (raiz || document).querySelectorAll('.mt-acordeon');
    if (!acordeones.length) return;

    acordeones.forEach(function (ac) {
      if (ac.dataset.mtListo === '1') return;
      ac.dataset.mtListo = '1';

      var items = Array.prototype.slice.call(ac.querySelectorAll('.mt-acordeon-item'));

      items.forEach(function (item) {
        var boton = item.querySelector('.mt-acordeon-boton');
        var cuerpo = item.querySelector('.mt-acordeon-cuerpo');
        if (!boton || !cuerpo) return;

        boton.addEventListener('click', function () {
          var abierto = item.classList.contains('mt-acordeon-item--abierto');

          if (ac.dataset.mtUnico !== 'false') {
            items.forEach(function (otro) {
              if (otro === item) return;
              otro.classList.remove('mt-acordeon-item--abierto');
              var b = otro.querySelector('.mt-acordeon-boton');
              var c = otro.querySelector('.mt-acordeon-cuerpo');
              if (b) b.setAttribute('aria-expanded', 'false');
              if (c) c.style.maxHeight = '0px';
            });
          }

          item.classList.toggle('mt-acordeon-item--abierto', !abierto);
          boton.setAttribute('aria-expanded', String(!abierto));
          cuerpo.style.maxHeight = !abierto ? cuerpo.scrollHeight + 'px' : '0px';
        });
      });
    });
  }

  /* --- Parallax suave (solo transform) ---------------------------------- */

  function initParallax(raiz) {
    var capas = Array.prototype.slice.call(
      (raiz || document).querySelectorAll('[data-mt-parallax]')
    );
    if (!capas.length || reduceMotion) return;
    if (window.matchMedia('(max-width: 990px)').matches) return;

    var pendiente = false;

    function pintar() {
      pendiente = false;
      var vh = window.innerHeight;
      capas.forEach(function (capa) {
        var rect = capa.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        var fuerza = parseFloat(capa.getAttribute('data-mt-parallax')) || 0;
        var centro = rect.top + rect.height / 2 - vh / 2;
        capa.style.transform = 'translate3d(0,' + (-centro * fuerza * 0.06).toFixed(2) + 'px,0)';
      });
    }

    function alScroll() {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(pintar);
    }

    window.addEventListener('scroll', alScroll, { passive: true });
    window.addEventListener('resize', alScroll);
    pintar();
  }

  /* --- (Retirado) Escena sticky ----------------------------------------
     La escena pasó a ser bloques con la imagen AL LADO de su texto, así que
     ya no hace falta sincronizar nada: entran con los reveals normales.
     Se conserva la función vacía por si alguna sección antigua la llama. */

  /* --- Página de producto: galería + variantes -------------------------- */

  function initProducto(raiz) {
    var fichas = (raiz || document).querySelectorAll('[data-mt-producto]');
    if (!fichas.length) return;

    fichas.forEach(function (ficha) {
      if (ficha.dataset.mtListo === '1') return;
      ficha.dataset.mtListo = '1';

      /* ---- Galería ---- */
      var slides = Array.prototype.slice.call(ficha.querySelectorAll('.mt-pd-slide'));
      var minis = Array.prototype.slice.call(ficha.querySelectorAll('.mt-pd-miniatura'));

      function irA(i) {
        if (i < 0 || i >= slides.length) return;
        slides.forEach(function (s, j) { s.classList.toggle('mt-pd-slide--activa', j === i); });
        minis.forEach(function (m, j) {
          m.classList.toggle('mt-pd-miniatura--activa', j === i);
          if (j === i && m.scrollIntoView) {
            m.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reduceMotion ? 'auto' : 'smooth' });
          }
        });
      }

      minis.forEach(function (m) {
        m.addEventListener('click', function () { irA(parseInt(m.getAttribute('data-mt-ir'), 10)); });
      });

      /* ---- Variantes ---- */
      var datos = ficha.querySelector('[data-mt-variantes]');
      var inputId = ficha.querySelector('[data-mt-variante-id]');
      var boton = ficha.querySelector('[data-mt-comprar]');
      var botonTexto = ficha.querySelector('[data-mt-comprar-texto]');
      var precioEl = ficha.querySelector('[data-mt-precio]');
      var antesEl = ficha.querySelector('[data-mt-antes]');
      if (!datos || !inputId) return;

      var variantes;
      try { variantes = JSON.parse(datos.textContent); } catch (e) { return; }
      if (!Array.isArray(variantes) || !variantes.length) return;

      var opciones = Array.prototype.slice.call(ficha.querySelectorAll('[data-mt-opcion]'));
      var botones = Array.prototype.slice.call(ficha.querySelectorAll('[data-mt-valor]'));
      if (!botones.length) return;

      /* Estado inicial: lo que ya viene marcado desde Liquid */
      var seleccion = [];
      botones.forEach(function (b) {
        var i = parseInt(b.getAttribute('data-mt-indice'), 10);
        if (b.getAttribute('aria-pressed') === 'true') seleccion[i] = b.getAttribute('data-mt-valor');
      });

      var textoAgotado = boton ? (boton.getAttribute('data-mt-agotado') || '') : '';
      var textoNormal = boton ? (boton.getAttribute('data-mt-normal') || '') : '';
      if (!textoNormal && botonTexto) textoNormal = botonTexto.textContent.trim();

      /* Precios ya formateados por Shopify (mismo formato que el servidor) */
      var precios = {};
      var datosPrecios = ficha.querySelector('[data-mt-precios]');
      if (datosPrecios) {
        try { precios = JSON.parse(datosPrecios.textContent); } catch (e) { precios = {}; }
      }

      function formatearDinero(centimos) {
        /* Respaldo solo si faltara el dato formateado */
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'es-ES', {
            style: 'currency',
            currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR'
          }).format(centimos / 100);
        } catch (e) {
          return (centimos / 100).toFixed(2).replace('.', ',') + ' €';
        }
      }

      function precioDe(v, campo) {
        var fila = precios[String(v.id)];
        if (fila && fila[campo]) return fila[campo];
        return formatearDinero(campo === 'antes' ? v.compare_at_price : v.price);
      }

      function buscarVariante() {
        for (var i = 0; i < variantes.length; i++) {
          var v = variantes[i];
          var coincide = true;
          for (var j = 0; j < seleccion.length; j++) {
            if (seleccion[j] === undefined) continue;
            if (v.options[j] !== seleccion[j]) { coincide = false; break; }
          }
          if (coincide) return v;
        }
        return null;
      }

      function pintarNombres() {
        opciones.forEach(function (op) {
          var i = parseInt(op.getAttribute('data-mt-opcion'), 10);
          var destino = ficha.querySelector('[data-mt-nombre-opcion="' + i + '"]');
          if (!destino) return;
          var activo = botones.filter(function (b) {
            return parseInt(b.getAttribute('data-mt-indice'), 10) === i &&
                   b.getAttribute('aria-pressed') === 'true';
          })[0];
          destino.textContent = activo ? (activo.getAttribute('data-mt-etiqueta') || '') : '';
        });
      }

      function actualizar() {
        var v = buscarVariante();
        if (!v) return;

        inputId.value = v.id;

        if (precioEl) precioEl.textContent = precioDe(v, 'precio');
        if (antesEl) {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            antesEl.hidden = false;
            var s = antesEl.querySelector('s');
            if (s) s.textContent = precioDe(v, 'antes');
          } else {
            antesEl.hidden = true;
          }
        }

        if (boton) {
          boton.disabled = !v.available;
          if (botonTexto && textoAgotado) {
            botonTexto.textContent = v.available ? textoNormal : textoAgotado;
          }
        }

        /* Si la variante tiene foto propia, salta a ella en la galería */
        if (v.featured_media && typeof v.featured_media.position === 'number') {
          irA(v.featured_media.position - 1);
        }

        pintarNombres();
      }

      botones.forEach(function (b) {
        b.addEventListener('click', function () {
          var i = parseInt(b.getAttribute('data-mt-indice'), 10);
          seleccion[i] = b.getAttribute('data-mt-valor');

          botones.forEach(function (otro) {
            if (parseInt(otro.getAttribute('data-mt-indice'), 10) !== i) return;
            var on = otro === b;
            otro.setAttribute('aria-pressed', on ? 'true' : 'false');
            otro.classList.toggle('mt-pd-muestra--activa', on && otro.classList.contains('mt-pd-muestra'));
            otro.classList.toggle('mt-pd-accesorio--activo', on && otro.classList.contains('mt-pd-accesorio'));
          });

          actualizar();
        });
      });

      pintarNombres();
    });
  }

  /* --- Arranque --------------------------------------------------------- */

  function iniciarTodo(raiz) {
    initReveals(raiz);
    initContadores(raiz);
    initComparadores(raiz);
    initPestanas(raiz);
    initSelectorColor(raiz);
    initAcordeon(raiz);
    initParallax(raiz);
    initProducto(raiz);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { iniciarTodo(document); });
  } else {
    iniciarTodo(document);
  }

  /* El editor de Shopify recarga secciones sueltas: re-inicializa esa parte */
  document.addEventListener('shopify:section:load', function (e) {
    iniciarTodo(e.target);
  });
})();
